<template>
  <transition name="slide-fade">
    <CommunityCard class="mb-4">
      <template v-if="isMyScenario">
        <div class="info-row" v-if="!scenario.is_processed">
          <em>
            This scenario is currently processing. please check back later.
          </em>
        </div>
        <div
          class="info-row"
          v-if="scenario.is_processed && !scenario.is_publishable"
        >
          <router-link
            :to="{
              name: 'scenario-edit',
              params: { uuidId: scenario.uuid_id },
            }"
          >
            <em>
              This scenario is unpublished. Click here to add a description,
              categories and tags.
            </em>
          </router-link>
        </div>
      </template>

      <div
        v-if="scenario.is_processed"
        class="p-3 d-flex flex-wrap justify-content-between"
      >
        <div class="d-flex left-content w-100">
          <div class="mr-3 scenario-card-monitor">
            <router-link
              :to="{
                name: 'scenario-view',
                params: { uuidId: scenario.uuid_id },
              }"
            >
              <img
                alt="device thumbnail"
                style="width: 100%"
                class="isim-rounded"
                :src="isCtgi ? ctgiThumb : deviceThumb"
              />
            </router-link>

            <div
              v-if="isMyScenario && scenario.is_publishable"
              class="btn-group personal-settings w-100 mt-3"
              role="group"
              aria-label="Basic example"
            >
              <button
                :class="{
                  'btn btn-light': true,
                  selected: scenario.privacy_level === PRIVACY_LEVELS.PUBLIC,
                }"
                @click="changePrivacyLevel(PRIVACY_LEVELS.PUBLIC)"
              >
                <i class="material-icons privacy">lock_open</i> Public
              </button>
              <button
                :class="{
                  'btn btn-light': true,
                  selected: scenario.privacy_level === PRIVACY_LEVELS.PRIVATE,
                }"
                @click="changePrivacyLevel(PRIVACY_LEVELS.PRIVATE)"
              >
                <i class="material-icons privacy">lock</i> Private
              </button>
            </div>
            <div class="w-100 mt-3" v-if="isMyScenario">
              <a v-if="!isOnNative" :href="webEditorUrl" class="w-100 mt-2">
                <button class="w-100 btn btn-light">
                  <img
                    alt="open scenario"
                    src="../assets/share.svg"
                    class="pr-1 pb-1"
                  />
                  <br />
                  Open scenario
                </button>
              </a>
            </div>
            <div
              class="w-100 mt-3"
              v-if="myUserId !== scenario.contributed_by.id"
            >
              <button class="w-100 btn btn-light" @click="addToMyScenarios">
                <img
                  alt="add scenario"
                  src="../assets/upload-primary.svg"
                  class="pr-1 pb-1"
                />
                {{ addButtonText }}
              </button>
            </div>
          </div>

          <div
            class="scenario-card-details d-flex flex-column justify-content-between w-100"
          >
            <div class="d-flex flex-wrap justify-content-between">
              <div>
                <h4 class="font-weight-bold mb-2">
                  <router-link
                    :to="{
                      name: 'scenario-view',
                      params: { uuidId: scenario.uuid_id },
                    }"
                  >
                    {{ scenario.name }}
                  </router-link>
                </h4>
                <div>
                  <div
                    class="d-inline time-text"
                    data-toggle="tooltip"
                    :title="getFullDate()"
                  >
                    {{ getPrettyDate() }}
                    <router-link
                      v-if="!isMyScenario"
                      :to="{
                        name: 'view-user',
                        params: { id: contributedByUuid },
                      }"
                    >
                      by
                      {{
                        scenario.contributed_by.first_name +
                        " " +
                        scenario.contributed_by.last_name
                      }}
                    </router-link>
                  </div>
                  &middot;
                  {{
                    humanFileSize && humanFileSize !== "null"
                      ? humanFileSize + " &middot;"
                      : ""
                  }}
                  {{ scenario.num_downloads }} Downloads &middot;
                  {{ scenario.number_favourites }} Favourites
                </div>
                <p class="my-2 description">{{ scenario.description }}</p>
                <div v-if="scenario.tags.length">
                  <ul class="px-0 my-3 d-flex flex-wrap list-style-none">
                    <li
                      v-bind:key="tag.id"
                      v-for="tag in scenario.tags"
                      v-bind:class="{
                        'tag my-1': true,
                        selected: tag.isCurrentlySelected,
                      }"
                    >
                      {{ tag.title }}
                    </li>
                  </ul>
                </div>
              </div>
              <ScenarioActions
                :ctgiScenario="isCtgi"
                :scenario="scenario"
              ></ScenarioActions>
            </div>

            <div>
              <ScenarioElements
                :language="scenario.unicode_language_identifier"
                :durationInSeconds="scenario.duration_in_seconds"
                :numChecklistItems="scenario.num_checklist_items"
                :numEvents="scenario.num_events"
                :hasMedia="scenario.has_video"
                :hasSounds="scenario.has_sounds"
                :hasLabs="scenario.has_labs"
                :isCtgi="isCtgi"
              ></ScenarioElements>
            </div>
          </div>
        </div>
      </div>
    </CommunityCard>
  </transition>
</template>

<script>
import ScenarioActions from "../components/ScenarioActions";
import ScenarioElements from "../components/ScenarioElements";
import CommunityCard from "../components/CommunityCard";
import ScenarioHelper from "../utils/scenario.helper";
import ScenService from "../services/scenarios.service";
import {
  getFormattedDateStringFromDateString,
  getScenarioPrettyDate,
} from "@/utils/datetime.helper";

export default {
  name: "Scenario",
  components: {
    ScenarioActions,
    ScenarioElements,
    CommunityCard,
  },
  data: function () {
    return {
      PRIVACY_LEVELS: {
        PRIVATE: 0,
        PUBLIC: 2,
      },
      isCtgi:
        this.scenario.object_url && this.scenario.object_url.includes(".ctgi"),
      ctgiThumb: require("../assets/device/CTGi.png"),
      deviceThumb: require("../assets/device/" +
        ScenarioHelper.getFilenameForDeviceThumb(this.scenario.device_id)),
      isSubmitting: false,
      addButtonText: "Add to My Scenarios",
    };
  },
  computed: {
    contributedBy() {
      return this.scenario.contributed_by != null
        ? `${this.scenario.contributed_by.first_name} ${this.scenario.contributed_by.last_name}`
        : "Unknown";
    },
    contributedByUuid() {
      return (
        this.scenario.contributed_by.uuid ||
        this.scenario.contributed_by.uuid_id ||
        ""
      );
    },
    webEditorUrl() {
      return (
        process.env.VUE_APP_CONTROL_DOMAIN +
        `?editscenarioid=${this.scenario.uuid_id}&editscenarioname=${this.scenario.name}`
      );
    },
    isOnNative() {
      return this.$store.getters.getIsOnNative;
    },
    humanFileSize() {
      if (isNaN(this.scenario.file_size) || this.scenario.file_size == null)
        return 0;

      const toKilo = this.scenario.file_size / 1000;
      return toKilo < 1
        ? this.scenario.file_size + " bytes"
        : toKilo > 1000
        ? Math.round(toKilo / 1000) + " MB"
        : Math.round(toKilo) + " KB";
    },
    myUserId() {
      return this.$store.getters.getUser.id;
    },
  },
  methods: {
    updateFavouriteScenariosList() {
      this.$parent.updateFavouriteScenariosList();
    },
    shouldTagBeSelected() {
      if (!this.scenario.tags || !this.currentlySelectedTags) return;

      this.scenario.tags.forEach((scenarioTag) => {
        this.currentlySelectedTags.forEach((selectedTag) => {
          if (scenarioTag.id === selectedTag) {
            scenarioTag["isCurrentlySelected"] = true;
          }
        });
      });
    },
    changePrivacyLevel(newLevel) {
      if (newLevel === this.scenario.privacy_level) return;

      const privateTitle = "Make private?";
      const publicTitle = "Make public?";
      const privateMessage =
        "Private scenarios are <b>not</b> visible in Scenarios, but may be attached to Group Posts.";
      const publicMessage =
        "Public scenarios are <b>publicly visible</b> in Scenarios.";

      global.vm.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-primary px-4",
            cancelButton: "btn btn-danger",
          },
          buttonsStyling: false,
        })
        .fire({
          title:
            newLevel === this.PRIVACY_LEVELS.PUBLIC
              ? publicTitle
              : privateTitle,
          html:
            newLevel === this.PRIVACY_LEVELS.PUBLIC
              ? publicMessage
              : privateMessage,
          showCancelButton: true,
          confirmButtonText: "Ok",
        })
        .then((result) => {
          if (result.value) {
            let data = { privacy_level: newLevel };
            ScenService.editScenario(data, this.scenario.uuid_id).then(
              (response) => {
                this.scenario.privacy_level = Number(
                  response.updatedScenario.privacy_level
                );
              }
            );
          }
        });
    },
    getFullDate() {
      return getFormattedDateStringFromDateString(this.scenario.contributed_at);
    },
    getPrettyDate() {
      return getScenarioPrettyDate(this.scenario.contributed_at);
    },
    addToMyScenarios() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      this.addButtonText = "...";

      ScenService.copyToMyScenarios(this.scenario.uuid_id)
        .then(() => {
          vm.$swal.fire(
            "Success",
            "You have saved a copy of " + this.scenario.name,
            "success"
          );
        })
        .catch(() => {
          vm.$swal.fire("Fail", "Couldn't copy scenario", "fail");
        })
        .finally(() => {
          this.isSubmitting = false;
          this.addButtonText = "Add to My Scenarios";
        });
    },
  },
  props: {
    scenario: Object,
    currentlySelectedTags: Array,
    isMyScenario: Boolean,
  },
  beforeMount() {
    this.shouldTagBeSelected();
  },
  beforeUpdate() {
    this.shouldTagBeSelected();
  },
};
</script>

<style scoped>
.scenario-card-monitor {
  width: 15%;
  min-width: 150px;
}

.scenario-card-details {
  font-size: 0.87rem;
  color: var(--isim-light-text);
}

.scenario-card-details .description {
  color: var(--isim-text);
}

.scenario-card-details .tag {
  background: var(--isim-primary);
  color: var(--light);
  padding: 6px;
  margin-right: 8px;
  border-radius: 4px;
}

.info-row {
  background: var(--isim-warning);
  padding: 10px;
  cursor: pointer;
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  color: inherit;
}

.info-row a,
.info-row a:visited {
  color: inherit;
}

.personal-settings .btn {
  flex: 1;
}

.btn.selected {
  background: var(--isim-primary);
  color: white;
}

.btn.selected i.privacy {
  color: white;
}

i.privacy {
  font-size: 1rem;
  color: var(--isim-primary);
  vertical-align: middle;
  padding-bottom: 4px;
}
</style>
